import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { MessageSquare, TriangleAlert } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  usePostComment,
  useVideoComments,
  type VideoComment,
} from '@/hooks/useVideoComments';
import { genUserName } from '@/lib/genUserName';
import { timeAgo, type VideoData } from '@/lib/videoTypes';

function CommentAuthor({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? genUserName(pubkey);
  const npub = nip19.npubEncode(pubkey);

  return (
    <Link to={`/${npub}`} className="flex items-center gap-2 min-w-0">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={metadata?.picture} alt={name} loading="lazy" />
        <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="truncate text-sm font-medium hover:text-primary transition-colors">
        {name}
      </span>
    </Link>
  );
}

function CommentItem({
  comment,
  onReply,
  depth,
}: {
  comment: VideoComment;
  onReply: (parent: VideoComment) => void;
  depth: number;
}) {
  const { user } = useCurrentUser();

  return (
    <div className={depth > 0 ? 'ml-8 mt-3 border-l-2 border-border pl-3' : ''}>
      <div className="flex items-center gap-2 flex-wrap">
        <CommentAuthor pubkey={comment.event.pubkey} />
        <span className="text-xs text-muted-foreground">
          {timeAgo(comment.event.created_at)}
        </span>
        {comment.standard === 'legacy' && (
          <Badge
            variant="outline"
            className="border-yellow-600/50 text-yellow-600 dark:text-yellow-500 text-[10px] px-1.5 py-0"
            title="This comment uses kind 1, which is not the standardized comment kind. NIP-22 defines kind 1111 for comments."
          >
            Non-standard (kind 1)
          </Badge>
        )}
      </div>
      <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed">
        {comment.event.content}
      </p>
      {user && (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => onReply(comment)}
        >
          Reply
        </button>
      )}
      {comment.replies.map((reply) => (
        <CommentItem key={reply.event.id} comment={reply} onReply={onReply} depth={depth + 1} />
      ))}
    </div>
  );
}

function CommentComposer({
  video,
  replyTo,
  onCancelReply,
  onPosted,
}: {
  video: VideoData;
  replyTo: VideoComment | null;
  onCancelReply: () => void;
  onPosted: () => void;
}) {
  const { user } = useCurrentUser();
  const postComment = usePostComment(video);
  const [content, setContent] = useState('');
  const [kind, setKind] = useState<'1111' | '1'>('1111');

  if (!user) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          Log in to write a comment.
        </CardContent>
      </Card>
    );
  }

  const submit = () => {
    if (!content.trim() || postComment.isPending) return;
    postComment.mutate(
      {
        content,
        kind: kind === '1111' ? 1111 : 1,
        parent: replyTo ?? undefined,
      },
      {
        onSuccess: () => {
          setContent('');
          onPosted();
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      {replyTo && (
        <div className="flex items-center justify-between rounded-md bg-secondary px-3 py-2 text-sm">
          <span className="truncate text-muted-foreground">
            Replying to: {replyTo.event.content.slice(0, 80)}
          </span>
          <Button variant="ghost" size="sm" onClick={onCancelReply}>
            Cancel
          </Button>
        </div>
      )}

      <Textarea
        placeholder="Add a comment…"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        className="resize-none"
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <RadioGroup
          value={kind}
          onValueChange={(v) => setKind(v === '1' ? '1' : '1111')}
          className="flex items-center gap-4"
        >
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="1111" id="kind-1111" />
            <Label htmlFor="kind-1111" className="text-sm font-normal cursor-pointer">
              Comment (NIP-22, kind 1111)
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="1" id="kind-1" />
            <Label htmlFor="kind-1" className="text-sm font-normal cursor-pointer">
              Note (kind 1)
            </Label>
          </div>
        </RadioGroup>

        <Button onClick={submit} disabled={!content.trim() || postComment.isPending} size="sm">
          {postComment.isPending ? 'Posting…' : 'Post comment'}
        </Button>
      </div>

      {kind === '1' && (
        <Alert variant="destructive" className="border-yellow-600/50 bg-yellow-600/10 text-yellow-700 dark:text-yellow-500 [&>svg]:text-yellow-600 dark:[&>svg]:text-yellow-500">
          <TriangleAlert className="h-4 w-4" />
          <AlertDescription className="text-yellow-700/90 dark:text-yellow-500/90 text-xs">
            Kind 1 notes are <strong>not</strong> the standardized comment kind.
            NIP-22 defines kind 1111 for comments, which lets clients render
            threaded comment sections correctly. Use kind 1 only if you know
            why you need it.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export function VideoComments({ video }: { video: VideoData }) {
  const comments = useVideoComments(video);
  const [replyTo, setReplyTo] = useState<VideoComment | null>(null);

  const count = comments.data?.reduce(
    (sum, c) => sum + 1 + c.replies.length,
    0,
  ) ?? 0;

  return (
    <section className="space-y-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <MessageSquare className="h-5 w-5" />
        Comments
        {comments.data && <span className="text-sm font-normal text-muted-foreground">({count})</span>}
      </h2>

      <CommentComposer
        video={video}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onPosted={() => setReplyTo(null)}
      />

      {comments.isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-8 w-8 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : comments.data && comments.data.length > 0 ? (
        <div className="space-y-5">
          {comments.data.map((comment) => (
            <CommentItem
              key={comment.event.id}
              comment={comment}
              onReply={setReplyTo}
              depth={0}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No comments yet. Be the first to comment!
          </CardContent>
        </Card>
      )}
    </section>
  );
}
